import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import PostCard from '../../components/PostCard/PostCard';
import DialogueThemePanel from '../../components/DialogueThemePanel/DialogueThemePanel';
import { characterApi } from '../../services/characterApi';
import dialogueThemeService from '../../services/dialogueThemeService';
import { CHARACTER_THEMES, normalizeSheetTheme, useCharacterSiteTheme } from '../../constants/characterThemes';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import { buildRpPreviewContent } from '../../utils/dialogueTheme';
import sheetStyles from '../ThreadDetail/ThreadDetail.module.css';
import '../ThreadDetail/themes.css';
import styles from './CharacterTheme.module.css';

const PREVIEW_MODES = [
  { id: 'rp', label: 'Post RP' },
  { id: 'presentation', label: 'Fiche de présentation' },
  { id: 'internal', label: 'Fiche interne' },
];

const PREVIEW_GENERAL_INFO = [
  { label: 'Univers', value: 'DC Comics' },
  { label: 'Occupation', value: 'Justicier masqué' },
  { label: 'Affiliation morale', value: 'Héros' },
];

const PREVIEW_BIO = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.';

const CharacterTheme = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [previewMode, setPreviewMode] = useState('rp');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [dialogueThemes, setDialogueThemes] = useState([]);
  const [activeDialogueThemeId, setActiveDialogueThemeId] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [profileData, dialogueData] = await Promise.all([
        characterApi.getCharacterProfile(id),
        dialogueThemeService.listThemes(),
      ]);
      setProfile(profileData);
      setSelectedTheme(normalizeSheetTheme(profileData?.character?.sheetTheme));
      const themes = dialogueData?.items || [];
      setDialogueThemes(themes);
      const defaultTheme = themes.find((theme) => theme.isDefault) || themes[0];
      setActiveDialogueThemeId(defaultTheme ? String(defaultTheme.id) : '');
    } catch (err) {
      setError('Erreur lors du chargement du personnage');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useCharacterSiteTheme(selectedTheme);

  const character = profile?.character;

  const activeDialogueTheme = useMemo(() => {
    if (!activeDialogueThemeId) return dialogueThemes[0] || null;
    return dialogueThemes.find((theme) => String(theme.id) === String(activeDialogueThemeId)) || dialogueThemes[0] || null;
  }, [dialogueThemes, activeDialogueThemeId]);

  const previewPost = useMemo(() => {
    const content = buildRpPreviewContent(activeDialogueTheme || { id: 'preview', color: '#339999' });
    return {
      id: 'preview',
      content,
      date: 'Aperçu — maintenant',
      author: character?.name || 'Personnage',
      authorId: null,
      avatar: character?.avatar || null,
    };
  }, [activeDialogueTheme, character]);

  const hasChanges = character && selectedTheme !== normalizeSheetTheme(character.sheetTheme);

  const handleSave = async () => {
    if (!character) return;
    setSaving(true);
    setSaveMessage('');
    try {
      await characterApi.updateCharacter(character.id, { sheetTheme: selectedTheme });
      setSaveMessage('Thème du personnage enregistré.');
      await loadData();
    } catch (err) {
      setSaveMessage(err.response?.data?.error || 'Erreur lors de l\'enregistrement du thème');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement du thème..." />
      </Layout>
    );
  }

  if (error || !profile || !character) {
    return (
      <Layout wide>
        <ErrorMessage message={error || 'Personnage introuvable'} onRetry={loadData} />
      </Layout>
    );
  }

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    { name: 'Mes Personnages', url: '/characters', icon: 'character' },
    { name: character.name, url: getCharacterProfilePath(character) },
    { name: 'Thème du personnage', url: null },
  ];

  const renderSheetPreview = (variant) => (
    <div className={styles.sheetPreview} data-theme={selectedTheme}>
      <div className={sheetStyles.characterSheet}>
        <div className={sheetStyles.characterHeaderWithAvatar}>
          <div className={sheetStyles.characterHeaderAvatar}>
            {character.avatar ? (
              <img src={character.avatar} alt={character.name} className={styles.sheetPreviewAvatar} />
            ) : (
              <div className={styles.sheetPreviewAvatarPlaceholder} aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            )}
          </div>
          <div className={sheetStyles.characterHeaderBody}>
            <h2 className={sheetStyles.characterTitle}>{character.name}</h2>
            {(character.firstName || character.lastName) && (
              <p className={sheetStyles.characterSubtitle}>
                {[character.firstName, character.lastName].filter(Boolean).join(' ')}
              </p>
            )}
            {character.alias && (
              <p className={sheetStyles.characterAlias}>{character.alias}</p>
            )}
          </div>
        </div>

        {variant === 'internal' && (
          <div className={styles.internalPreviewExtra}>
            <div className={styles.internalPreviewStats}>
              <span className={styles.statPill}>
                <strong>{profile.stats?.postCount ?? 0}</strong> posts
              </span>
              <span className={styles.statPill}>
                <strong>{profile.stats?.rpThreadCount ?? 0}</strong> scènes RP
              </span>
            </div>
          </div>
        )}

        <div className={sheetStyles.characterInfo}>
          <section className={sheetStyles.generalInfoSection}>
            <h3 className={sheetStyles.generalInfoTitle}>Informations générales</h3>
            <dl className={sheetStyles.infoList}>
              {(variant === 'internal'
                ? [
                  character.universe && { label: 'Univers', value: character.universe.name },
                  character.occupation && { label: 'Occupation', value: character.occupation },
                  character.moralAffiliation && { label: 'Affiliation morale', value: character.moralAffiliation },
                ].filter(Boolean)
                : PREVIEW_GENERAL_INFO
              ).map((field) => (
                <div key={field.label} className={sheetStyles.infoListRow}>
                  <dt className={sheetStyles.infoLabel}>{field.label}</dt>
                  <dd className={sheetStyles.infoValue}>{field.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div className={sheetStyles.tabsWrapper}>
            <div className={sheetStyles.tabs} role="tablist">
              <button type="button" className={`${sheetStyles.tab} ${sheetStyles.tabActive}`} role="tab">
                Biographie
              </button>
              <button type="button" className={sheetStyles.tab} role="tab">
                Personnalité
              </button>
            </div>
          </div>
          <div className={sheetStyles.tabContent} role="tabpanel">
            <div className={sheetStyles.characterSection}>
              <div className={sheetStyles.sectionContent}>
                <p>{PREVIEW_BIO}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreview = () => {
    if (previewMode === 'rp') {
      return (
        <>
          <p className={styles.previewHint}>
            Aperçu d&apos;un post RP avec dialogues colorés. Le thème de dialogue est indépendant du thème du personnage.
          </p>
          <PostCard
            post={previewPost}
            isRoleplay
            character={{
              ...character,
              sheetTheme: selectedTheme,
              factions: character.factions || [],
              playerPseudo: profile.player?.pseudo,
              playerIsAnonymous: profile.player?.isAnonymous,
            }}
          />
          <div className={styles.dialogueSection}>
            <DialogueThemePanel
              dialogueThemes={dialogueThemes}
              onDialogueThemesChange={setDialogueThemes}
              activeDialogueThemeId={activeDialogueThemeId}
              onActiveDialogueThemeIdChange={setActiveDialogueThemeId}
              idPrefix="character-theme"
            />
          </div>
        </>
      );
    }

    if (previewMode === 'presentation') {
      return (
        <>
          <p className={styles.previewHint}>
            Aperçu de la fiche de présentation publique (thread personnage).
          </p>
          {renderSheetPreview('presentation')}
        </>
      );
    }

    return (
      <>
        <p className={styles.previewHint}>
          Aperçu de la fiche interne avec statistiques et onglets.
        </p>
        {renderSheetPreview('internal')}
      </>
    );
  };

  return (
    <Layout wide>
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <header className={styles.header}>
          <h1 className={styles.title}>Thème du personnage</h1>
          <p className={styles.subtitle}>
            Personnalise l&apos;apparence de <strong>{character.name}</strong> : fiche de présentation, fiche interne et posts RP.
          </p>
        </header>

        <div className={styles.layout}>
          <aside className={styles.themePanel}>
            <h2 className={styles.themePanelTitle}>Choisir un thème</h2>
            <div className={styles.themeList}>
              {CHARACTER_THEMES.map((theme) => (
                <label
                  key={theme.id}
                  className={`${styles.themeOption} ${selectedTheme === theme.id ? styles.themeOptionActive : ''}`}
                >
                  <input
                    type="radio"
                    name="character-theme"
                    value={theme.id}
                    checked={selectedTheme === theme.id}
                    onChange={() => setSelectedTheme(theme.id)}
                  />
                  <span className={styles.themeOptionBody}>
                    <span className={styles.themeOptionLabel}>{theme.label}</span>
                    <span className={styles.themeOptionDescription}>{theme.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Enregistrement...' : 'Appliquer le thème'}
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => navigate(getCharacterProfilePath(character))}
              >
                Retour à la fiche
              </button>
            </div>
            {saveMessage && (
              <p className={`${styles.message} ${saveMessage.includes('Erreur') ? styles.messageError : styles.messageSuccess}`}>
                {saveMessage}
              </p>
            )}
          </aside>

          <section className={styles.previewPanel}>
            <div className={styles.previewToggles} role="tablist" aria-label="Éléments à prévisualiser">
              {PREVIEW_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  role="tab"
                  aria-selected={previewMode === mode.id}
                  className={`${styles.previewToggle} ${previewMode === mode.id ? styles.previewToggleActive : ''}`}
                  onClick={() => setPreviewMode(mode.id)}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <div className={styles.previewArea}>
              {renderPreview()}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default CharacterTheme;
